import React, { useEffect, useMemo, useState } from 'react';
import { Select, Loader } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useLocationSearchQuery } from '../../hooks/useLocationQueries';
import type { Location } from '../../types';

export interface LocationSearchSelectProps {
  value: Location | null;
  onChange: (location: Location | null) => void;
  disabled?: boolean;
  error?: string;
  'data-cy'?: string;
}

export const LocationSearchSelect: React.FC<LocationSearchSelectProps> = ({
  value,
  onChange,
  disabled = false,
  error,
  'data-cy': dataCy = 'membership-visit-location',
}) => {
  const [search, setSearch] = useState(value?.displayName ?? '');
  const [debouncedSearch] = useDebouncedValue(search, 300);

  useEffect(() => {
    setSearch(value?.displayName ?? '');
  }, [value]);

  const { data: locations = [], isLoading } = useLocationSearchQuery(
    debouncedSearch,
    { enabled: !disabled }
  );

  const locationById = useMemo(() => {
    const map = new Map<string, Location>();
    for (const loc of locations) {
      map.set(String(loc.id), loc);
    }
    if (value) {
      map.set(String(value.id), value);
    }
    return map;
  }, [locations, value]);

  const selectData = useMemo(() => {
    const items = locations.map(loc => ({
      value: String(loc.id),
      label: loc.displayName,
    }));
    if (value && !items.some(item => item.value === String(value.id))) {
      items.unshift({
        value: String(value.id),
        label: value.displayName,
      });
    }
    return items;
  }, [locations, value]);

  return (
    <Select
      label="City"
      placeholder="Search cities..."
      searchable
      clearable
      disabled={disabled}
      error={error}
      data={selectData}
      value={value ? String(value.id) : null}
      searchValue={search}
      onSearchChange={setSearch}
      onChange={selectedId => {
        if (!selectedId) {
          onChange(null);
          setSearch('');
          return;
        }
        const loc = locationById.get(selectedId) ?? null;
        onChange(loc);
        if (loc) {
          setSearch(loc.displayName);
        }
      }}
      nothingFoundMessage={isLoading ? 'Searching...' : 'No cities found'}
      rightSection={isLoading ? <Loader size="xs" /> : undefined}
      data-cy={dataCy}
    />
  );
};
